/**
 * Attention Center — client-side state store.
 *
 * Persisted in localStorage per the "zero backend changes" constraint.
 * Three keyed maps + a rolling history log. All items are keyed by the
 * aggregated `AttentionItem.id` (e.g., "readiness:xyz", "soc:1234") so
 * state survives across refreshes and re-aggregations.
 *
 * Namespaced under `hbf.attention.*` so it never collides with legacy
 * `vocalis_*` keys or the Owner Shell store.
 */
const KEY_SEEN     = "hbf.attention.seen.v1";      // { itemId: markedAtISO }
const KEY_SNOOZED  = "hbf.attention.snoozed.v1";   // { itemId: snoozeUntilISO }
const KEY_HISTORY  = "hbf.attention.history.v1";   // [ { ts, action, id, title, source, severity, ok, error } ]
const HISTORY_MAX  = 200;

function readMap(key) {
  try { return JSON.parse(localStorage.getItem(key) || "{}"); } catch { return {}; }
}
function writeMap(key, v) {
  try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* quota */ }
}
function readArr(key) {
  try { const v = JSON.parse(localStorage.getItem(key) || "[]"); return Array.isArray(v) ? v : []; }
  catch { return []; }
}

/** ── Seen ─────────────────────────────────────────────────────────── */
export function getSeen()                { return readMap(KEY_SEEN); }
export function isSeen(id)               { return !!readMap(KEY_SEEN)[id]; }
export function markSeen(id) {
  const m = readMap(KEY_SEEN); m[id] = new Date().toISOString(); writeMap(KEY_SEEN, m);
}
export function unmarkSeen(id) {
  const m = readMap(KEY_SEEN); delete m[id]; writeMap(KEY_SEEN, m);
}

/** ── Snooze ──────────────────────────────────────────────────────── */
export function getSnoozes()             { return readMap(KEY_SNOOZED); }
export function isSnoozed(id) {
  const m = readMap(KEY_SNOOZED);
  const until = m[id]; if (!until) return false;
  if (new Date(until).getTime() > Date.now()) return true;
  // expired — clean up lazily
  delete m[id]; writeMap(KEY_SNOOZED, m); return false;
}
export function snoozeUntilISO(id)       { return readMap(KEY_SNOOZED)[id] || null; }
export function snooze(id, hours) {
  const m = readMap(KEY_SNOOZED);
  m[id] = new Date(Date.now() + hours * 3600 * 1000).toISOString();
  writeMap(KEY_SNOOZED, m);
}
export function unsnooze(id) {
  const m = readMap(KEY_SNOOZED); delete m[id]; writeMap(KEY_SNOOZED, m);
}

/** ── History ─────────────────────────────────────────────────────── */
export function getHistory() { return readArr(KEY_HISTORY); }
export function pushHistory(entry) {
  const arr = readArr(KEY_HISTORY);
  arr.unshift({ ts: new Date().toISOString(), ...entry });
  if (arr.length > HISTORY_MAX) arr.length = HISTORY_MAX;
  try { localStorage.setItem(KEY_HISTORY, JSON.stringify(arr)); } catch { /* quota */ }
}
export function clearHistory() {
  try { localStorage.removeItem(KEY_HISTORY); } catch { /* quota */ }
}
